import { SubAccount } from "@blinkbooks/types";
import { formatAmount } from "@/utils/expenseFormatters";

// Color mapping for different account types (for badge/label styling)
export const getAccountTypeColor = (type: string): { bg: string; text: string; border: string } => {
  if (!type) {
    return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
  }

  // Normalize the type string for comparison (lowercase, trim)
  const normalizedType = type.toLowerCase().trim();

  // Asset types
  if (normalizedType.includes('asset') || normalizedType.includes('fixed asset') || normalizedType.includes('current asset') || normalizedType.includes('other current asset') || normalizedType.includes('other asset')) {
    return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' };
  }

  // Bank types
  if (normalizedType.includes('bank') || normalizedType.includes('cash')) {
    return { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' };
  }

  // Liability types
  if (normalizedType.includes('liability') || normalizedType.includes('payable') || normalizedType.includes('credit card') || normalizedType.includes('current liability') || normalizedType.includes('long term liability')) {
    return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' };
  }

  // Equity types
  if (normalizedType.includes('equity') || normalizedType.includes('retained earnings')) {
    return { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' };
  }

  // Income/Revenue types
  if (normalizedType.includes('income') && !normalizedType.includes('expense') && !normalizedType.includes('other')) {
    return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' };
  }
  if (normalizedType.includes('revenue') || normalizedType.includes('sales')) {
    return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' };
  }

  // Expense types
  if (normalizedType.includes('expense') && !normalizedType.includes('other')) {
    return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' };
  }

  // Cost of Goods Sold
  if (normalizedType.includes('cost of goods sold') || normalizedType.includes('cogs')) {
    return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' };
  }

  // Other Income
  if (normalizedType.includes('other income') || normalizedType.includes('other revenue')) {
    return { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300' };
  }

  // Other Expense
  if (normalizedType.includes('other expense')) {
    return { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300' };
  }

  // Accounts Receivable
  if (normalizedType.includes('receivable')) {
    return { bg: 'bg-sky-100', text: 'text-sky-700', border: 'border-sky-300' };
  }

  // Default gray for unknown types
  return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
};

// ── BlinkBooks classification layer ─────────────────────────────────────────

export type BlinkBooksCategory =
  | "Cash"
  | "Receivables"
  | "Payables"
  | "Inventory"
  | "Other Assets"
  | "Clearing"
  | "Liabilities"
  | "Equity"
  | "Revenue"
  | "COGS"
  | "Operating Expenses";

export type AccountSection =
  | "Assets"
  | "Liabilities"
  | "Equity"
  | "Revenue"
  | "COGS"
  | "Operating Expenses";

const CLASSIFICATION: Record<string, { category: BlinkBooksCategory; section: AccountSection }> = {
  bank:                    { category: "Cash",               section: "Assets" },
  "accounts receivable":   { category: "Receivables",        section: "Assets" },
  "other current asset":   { category: "Other Assets",       section: "Assets" },
  "fixed asset":           { category: "Other Assets",       section: "Assets" },
  "other asset":           { category: "Other Assets",       section: "Assets" },
  "accounts payable":      { category: "Payables",           section: "Liabilities" },
  "credit card":           { category: "Liabilities",        section: "Liabilities" },
  "other current liability": { category: "Liabilities",      section: "Liabilities" },
  "long term liability":   { category: "Liabilities",        section: "Liabilities" },
  equity:                  { category: "Equity",             section: "Equity" },
  income:                  { category: "Revenue",            section: "Revenue" },
  "other income":          { category: "Revenue",            section: "Revenue" },
  "cost of goods sold":    { category: "COGS",               section: "COGS" },
  expense:                 { category: "Operating Expenses", section: "Operating Expenses" },
  "other expense":         { category: "Operating Expenses", section: "Operating Expenses" },
};

const norm = (t?: string | null) => (t ?? "").toLowerCase().trim();

// Accounts whose QB type says "Income" but are actually contra-revenue or expense items.
// QB doesn't distinguish these via AccountType — only the name gives it away.
const CONTRA_REVENUE_PATTERNS = ["discount", "return", "allowance", "refund", "rebate"];
const CLEARING_PATTERNS = ["clearing"];
const INVENTORY_PATTERNS = ["inventory"];

const isContraRevenue  = (name?: string | null): boolean => { const n = norm(name); return CONTRA_REVENUE_PATTERNS.some((p) => n.includes(p)); };
const isClearingAccount = (name?: string | null): boolean => { const n = norm(name); return CLEARING_PATTERNS.some((p) => n.includes(p)); };
const isInventoryAccount = (name?: string | null): boolean => { const n = norm(name); return INVENTORY_PATTERNS.some((p) => n.includes(p)); };

export const getBlinkBooksCategory = (qboType: string, name?: string | null): BlinkBooksCategory => {
  if (isClearingAccount(name)) return "Clearing";
  if (isInventoryAccount(name)) return "Inventory";
  const base = CLASSIFICATION[norm(qboType)];
  if (base?.category === "Revenue" && isContraRevenue(name)) return "Operating Expenses";
  return base?.category ?? "Operating Expenses";
};

export const getAccountSection = (qboType: string, name?: string | null): AccountSection => {
  if (isClearingAccount(name)) return "Assets";
  if (isInventoryAccount(name)) return "Assets";
  const base = CLASSIFICATION[norm(qboType)];
  if (base?.section === "Revenue" && isContraRevenue(name)) return "Operating Expenses";
  return base?.section ?? "Operating Expenses";
};

export const isKeyAccount = (name: string, qboType: string): boolean => {
  const t = norm(qboType);
  const n = (name ?? "").toLowerCase();
  return (
    t === "bank" ||
    t === "accounts receivable" ||
    t === "accounts payable" ||
    t === "income" ||
    t === "cost of goods sold" ||
    n.includes("payroll") ||
    n.includes("salary") ||
    n.includes("salaries")
  );
};

export type StructuralRole = "Mapped" | "Clearing" | "Payroll" | "Non-operating";

export const getStructuralRole = (
  name: string,
  qboType: string
): { label: StructuralRole; bg: string; text: string; border: string } => {
  if (isClearingAccount(name)) {
    return { label: "Clearing",      bg: "bg-slate-50",  text: "text-slate-500",  border: "border-slate-200"  };
  }
  const n = norm(name);
  if (n.includes("payroll") || n.includes("salary") || n.includes("salaries")) {
    return { label: "Payroll",       bg: "bg-violet-50", text: "text-violet-600", border: "border-violet-200" };
  }
  const t = norm(qboType);
  if (t === "other income" || t === "other expense") {
    return { label: "Non-operating", bg: "bg-gray-50",   text: "text-gray-500",   border: "border-gray-200"   };
  }
  return   { label: "Mapped",        bg: "bg-emerald-50",text: "text-emerald-700",border: "border-emerald-200" };
};

export const getCategoryStyle = (
  category: BlinkBooksCategory
): { bg: string; text: string; border: string } => {
  const styles: Record<BlinkBooksCategory, { bg: string; text: string; border: string }> = {
    Cash:                 { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
    Receivables:          { bg: "bg-sky-50",     text: "text-sky-700",     border: "border-sky-200"     },
    Payables:             { bg: "bg-orange-50",  text: "text-orange-700",  border: "border-orange-200"  },
    Inventory:            { bg: "bg-cyan-50",    text: "text-cyan-700",    border: "border-cyan-200"    },
    "Other Assets":       { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200"    },
    Clearing:             { bg: "bg-slate-50",   text: "text-slate-500",   border: "border-slate-200"   },
    Liabilities:          { bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200"     },
    Equity:               { bg: "bg-purple-50",  text: "text-purple-700",  border: "border-purple-200"  },
    Revenue:              { bg: "bg-green-50",   text: "text-green-700",   border: "border-green-200"   },
    COGS:                 { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200"   },
    "Operating Expenses": { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200"    },
  };
  return styles[category];
};

export const renderSubAccountRows = (
  subAccounts: SubAccount[],
  parentIndex: number,
  level: number = 1,
  parentKey: string = ''
): JSX.Element[] => {
  const rows: JSX.Element[] = [];

  subAccounts.forEach((subAccount, subIndex) => {
    const paddingLeft = `${level * 1.5}rem`;
    const rowKey = `${parentKey}-${subAccount.id}-${subIndex}-${level}`;
    const typeColors = getAccountTypeColor(subAccount.type);

    rows.push(
      <tr
        key={rowKey}
        className="border-b border-border/40 bg-muted/5 hover:bg-muted/30 transition-all duration-150"
      >
        <td className="py-2 px-3" style={{ paddingLeft }}></td>
        <td className="py-2 px-3" style={{ paddingLeft }}>
          <div className="text-xs text-muted-foreground/80">{subAccount.accountNumber ?? '—'}</div>
        </td>
        <td className="py-2 px-3">
          <div className="text-sm font-normal text-foreground/90">{subAccount.name}</div>
        </td>
        <td className="py-2 px-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
            {subAccount.type}
          </span>
        </td>
        <td className="py-2 px-3 text-right text-sm font-medium text-foreground/90 tabular-nums">
          {subAccount.balance !== undefined
            ? formatAmount(subAccount.balance)
            : <span className="text-muted-foreground">—</span>}
        </td>
      </tr>
    );

    // Recursively render nested sub-accounts if they exist
    if (subAccount.subAccounts && subAccount.subAccounts.length > 0) {
      rows.push(...renderSubAccountRows(subAccount.subAccounts, parentIndex, level + 1, rowKey));
    }
  });

  return rows;
};

export const renderFlatSubAccountRows = (
  subAccounts: SubAccount[],
  parentKey: string = '',
  level: number = 1
): JSX.Element[] => {
  const rows: JSX.Element[] = [];

  subAccounts.forEach((subAccount, subIndex) => {
    const paddingLeft = `${level * 1.5}rem`;
    const rowKey = `${parentKey}-${subAccount.id}-${subIndex}-${level}`;
    const typeColors = getAccountTypeColor(subAccount.type);

    rows.push(
      <tr
        key={rowKey}
        className="border-b border-border/40 bg-muted/5 hover:bg-muted/10 transition-all duration-150"
      >
        <td className="py-2 px-3" style={{ paddingLeft }}>
          <div className="text-xs text-muted-foreground/80">{subAccount.accountNumber ?? '—'}</div>
        </td>
        <td className="py-2 px-3">
          <div className="text-sm font-normal text-foreground/90">{subAccount.name}</div>
        </td>
        <td className="py-2 px-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
            {subAccount.type}
          </span>
        </td>
        <td className="py-2 px-3 text-right text-sm font-medium text-foreground/90 tabular-nums">
          {subAccount.balance !== undefined
            ? formatAmount(subAccount.balance)
            : <span className="text-muted-foreground">—</span>}
        </td>
      </tr>
    );

    // Recursively render nested sub-accounts if they exist
    if (subAccount.subAccounts && subAccount.subAccounts.length > 0) {
      rows.push(...renderFlatSubAccountRows(subAccount.subAccounts, rowKey, level + 1));
    }
  });

  return rows;
};
